import { useNavigate } from '@tanstack/react-router';
import { addDays } from 'date-fns/addDays';
import { isToday } from 'date-fns/isToday';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfToday } from 'date-fns/startOfToday';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';

import { inputsDbAtoms } from '@/features/inputs-db/inputs-db.atoms';
import { Routes } from '@/types/routes';
import { friendlyFormatDate } from '@/utils/formats';

import {
  buildActivityDayShape,
  type ActivityDayShape,
  type FocusCard,
} from './activity-timeline.model';
import {
  useActivityTimelineQuery,
  type ActivityInterval,
  type ActivitySnapshot,
} from './activity-timeline.query';
import {
  anchoredScrollTop,
  DEFAULT_PX_PER_MINUTE,
  RAIL_SCROLL_INSET_PX,
  readStoredPxPerMinute,
  storePxPerMinute,
  ZOOM_LEVELS,
} from './activity-timeline.zoom';

/** An open interval is drawn up to now, so today is redrawn each minute while it is on screen. */
const useNow = (enabled: boolean) => {
  const [now, setNow] = useState(() => new Date());
  const [wasEnabled, setWasEnabled] = useState(enabled);

  // If the user returns to today, `now` restarts from the current time, instead of the time it had
  // when the user left today
  if (enabled !== wasEnabled) {
    setWasEnabled(enabled);
    setNow(new Date());
  }

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, [enabled]);

  return now;
};

export const useActivityDay = () => {
  const [day, setDay] = useState(startOfToday);
  // addDays walks the local calendar, so a DST day is correctly 23 or 25 hours long
  const dayEnd = useMemo(() => addDays(day, 1), [day]);
  const query = useActivityTimelineQuery(day, dayEnd);
  const snapshots = query.data?.snapshots;
  const intervals = query.data?.intervals;
  const showsToday = isToday(day);

  const now = useNow(showsToday);

  // Today is drawn at least up to the latest fetch, because a fetch between two minute ticks can
  // return an interval that opened after the last tick
  const shape = useMemo(
    () =>
      buildActivityDayShape(
        snapshots ?? [],
        intervals ?? [],
        day,
        dayEnd,
        showsToday
          ? new Date(Math.max(now.getTime(), query.dataUpdatedAt))
          : dayEnd
      ),
    [snapshots, intervals, day, dayEnd, now, showsToday, query.dataUpdatedAt]
  );

  return {
    day,
    dayEnd,
    shape,
    label: friendlyFormatDate(day),
    isToday: showsToday,
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    error: query.error,
    pick: (next: Date) => setDay(startOfDay(next)),
    goPrev: () => setDay(addDays(day, -1)),
    goNext: () => setDay(addDays(day, 1)),
    goToday: () => setDay(startOfToday()),
  };
};

export type ActivitySnapshotSelection = {
  kind: 'snapshot';
  card: FocusCard | null;
  snapshot: ActivitySnapshot;
};

export type ActivitySelection =
  | ActivitySnapshotSelection
  | {
      kind: 'interval';
      interval: ActivityInterval;
      card: FocusCard | null;
    }
  | {
      kind: 'group';
      cards: FocusCard[];
    };

type SelectedId =
  | { kind: 'snapshot'; id: number }
  | { kind: 'interval'; id: string }
  | { kind: 'group'; ids: string[] };

const capturedMs = (snapshot: ActivitySnapshot) =>
  new Date(snapshot.capturedAt).getTime();

const revealSnapshot = (id: number) =>
  requestAnimationFrame(() =>
    document
      .querySelector(`[data-snapshot-id="${id}"], [data-snapshot-ids~="${id}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  );

const anchorMs = (selection: ActivitySelection) =>
  selection.kind === 'snapshot'
    ? capturedMs(selection.snapshot)
    : selection.kind === 'group'
      ? selection.cards[0].startedAt.getTime()
      : new Date(selection.interval.start).getTime();

/**
 * The selection is stored as ids and looked up in the day's shape, so that the interval and its
 * card cannot disagree, and an id from another day resolves to nothing instead of needing to be
 * cleared.
 */
export const useActivitySelection = (shape: ActivityDayShape) => {
  const [selectedId, setSelectedId] = useState<SelectedId | null>(null);

  const index = useMemo(() => {
    const cards = shape.columns.flatMap((column) => column.cards);
    const cardByInterval = new Map(
      cards.map((card) => [card.interval.id, card])
    );
    const intervalById = new Map<string, ActivityInterval>([
      ...cards.map((card) => [card.interval.id, card.interval] as const),
      ...shape.columns.flatMap((column) =>
        [...column.observation, ...column.presence].map(
          (span) => [span.interval.id, span.interval] as const
        )
      ),
    ]);
    const bySnapshotId = new Map<number, ActivitySnapshotSelection>();
    for (const card of cards) {
      for (const snapshot of card.snapshots) {
        bySnapshotId.set(snapshot.id, { kind: 'snapshot', card, snapshot });
      }
    }
    for (const column of shape.columns) {
      for (const orphan of column.orphans) {
        bySnapshotId.set(orphan.id, {
          kind: 'snapshot',
          card: null,
          snapshot: orphan,
        });
      }
    }
    const snapshotsInOrder = [...bySnapshotId.values()].sort(
      (one, other) =>
        capturedMs(one.snapshot) - capturedMs(other.snapshot) ||
        one.snapshot.id - other.snapshot.id
    );
    return { cardByInterval, intervalById, bySnapshotId, snapshotsInOrder };
  }, [shape]);

  const resolve = (): ActivitySelection | null => {
    if (selectedId === null) return null;
    if (selectedId.kind === 'snapshot') {
      return index.bySnapshotId.get(selectedId.id) ?? null;
    }
    if (selectedId.kind === 'group') {
      const cards = selectedId.ids
        .map((id) => index.cardByInterval.get(id))
        .filter((card) => card !== undefined);
      return cards.length === 0 ? null : { kind: 'group', cards };
    }
    const interval = index.intervalById.get(selectedId.id);
    return interval === undefined
      ? null
      : {
          kind: 'interval',
          interval,
          card: index.cardByInterval.get(interval.id) ?? null,
        };
  };

  const selected = resolve();
  const intervalIds = new Set<string>(
    selected === null || selected.kind === 'snapshot'
      ? []
      : selected.kind === 'interval'
        ? [selected.interval.id]
        : selected.cards.map((card) => card.interval.id)
  );

  const selectSnapshot = (id: number) => {
    setSelectedId({ kind: 'snapshot', id });
    revealSnapshot(id);
  };

  /** An interval or a group is placed in the day's capture order at its start. */
  const selectAdjacentSnapshot = (step: 1 | -1) => {
    const { snapshotsInOrder } = index;
    let next: number;
    if (selected === null) {
      next = step === 1 ? 0 : snapshotsInOrder.length - 1;
    } else if (selected.kind === 'snapshot') {
      const current = snapshotsInOrder.findIndex(
        (entry) => entry.snapshot.id === selected.snapshot.id
      );
      next = current + step;
    } else {
      const anchor = anchorMs(selected);
      const found = snapshotsInOrder.findIndex(
        (entry) => capturedMs(entry.snapshot) >= anchor
      );
      const atOrAfter = found === -1 ? snapshotsInOrder.length : found;
      next = step === 1 ? atOrAfter : atOrAfter - 1;
    }
    const target = snapshotsInOrder[next];
    if (target !== undefined) selectSnapshot(target.snapshot.id);
  };

  return {
    selected,
    intervalIds,
    clear: () => setSelectedId(null),
    selectSnapshot,
    selectAdjacentSnapshot,
    selectInterval: (id: string) => setSelectedId({ kind: 'interval', id }),
    selectGroup: (cards: FocusCard[]) =>
      setSelectedId({
        kind: 'group',
        ids: cards.map((card) => card.interval.id),
      }),
  };
};

/**
 * A zoom keeps the time at the middle of the viewport in the middle. The offset is computed from
 * the scroll position before the change, and applied in a layout effect, because the rail has its
 * new height only after React commits the new scale.
 */
export const useActivityRailZoom = () => {
  const [pxPerMinute, setPxPerMinute] = useState(readStoredPxPerMinute);
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchoredOffset = useRef<number | null>(null);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const offset = anchoredOffset.current;
    anchoredOffset.current = null;
    if (scroller !== null && offset !== null) scroller.scrollTop = offset;
  }, [pxPerMinute]);

  const zoomTo = (next: number) => {
    const scroller = scrollRef.current;
    if (scroller !== null) {
      anchoredOffset.current = anchoredScrollTop({
        scrollTop: scroller.scrollTop,
        viewportHeight: scroller.clientHeight,
        insetPx: RAIL_SCROLL_INSET_PX,
        scale: next / pxPerMinute,
      });
    }
    setPxPerMinute(next);
    storePxPerMinute(next);
  };

  const level = ZOOM_LEVELS.indexOf(pxPerMinute);
  const zoomToLevel = (index: number) =>
    zoomTo(ZOOM_LEVELS[Math.min(Math.max(index, 0), ZOOM_LEVELS.length - 1)]);

  return {
    pxPerMinute,
    scrollRef,
    isDefault: pxPerMinute === DEFAULT_PX_PER_MINUTE,
    canZoomOut: level > 0,
    canZoomIn: level < ZOOM_LEVELS.length - 1,
    zoomOut: () => zoomToLevel(level - 1),
    zoomIn: () => zoomToLevel(level + 1),
    reset: () => zoomTo(DEFAULT_PX_PER_MINUTE),
  };
};

export const useScrollToNowOrStart = (
  scrollRef: RefObject<HTMLDivElement | null>,
  day: Date,
  isDrawn: boolean
) => {
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!isDrawn || scroller === null) return;
    const nowLine = scroller.querySelector<HTMLElement>('[data-now-line]');
    if (nowLine === null) {
      scroller.scrollTop = 0;
      return;
    }
    const lineTop =
      nowLine.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    scroller.scrollTop = lineTop - (scroller.clientHeight * 2) / 3;
  }, [scrollRef, day, isDrawn]);
};

export const useOpenSnapshotsForCard = () => {
  const navigate = useNavigate();

  return useCallback(
    (card: FocusCard) => {
      inputsDbAtoms.showInputsForTimeRange({
        app: card.capturedAppName ?? '',
        after: card.startedAt.toISOString(),
        // The card can list a capture timed after the end of its interval, so the range extends to
        // its latest capture
        before: new Date(
          Math.max(
            card.endedAt.getTime(),
            ...card.snapshots.map((snapshot) =>
              new Date(snapshot.capturedAt).getTime()
            )
          )
        ).toISOString(),
      });
      navigate({ to: Routes.Ingestion as string });
    },
    [navigate]
  );
};
